import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, getDocs, doc, updateDoc, addDoc, serverTimestamp, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../firebase/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { FaBox, FaTimes, FaShippingFast, FaCheckCircle, FaSpinner } from 'react-icons/fa';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const SHIPPING_FEE = 5.99; // Base shipping fee

const ClawWinningsShipping = ({ isOpen, onClose, onSuccess }) => {
  const { currentUser } = useAuth();
  const [pendingWinnings, setPendingWinnings] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('select'); // select, shipping, payment
  const [shippingInfo, setShippingInfo] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: ''
  });

  // Fetch pending winnings
  useEffect(() => {
    if (isOpen && currentUser) {
      fetchPendingWinnings();
    }
  }, [isOpen, currentUser]);

  const fetchPendingWinnings = async () => {
    setLoading(true);
    try {
      const playsRef = collection(db, 'users', currentUser.uid, 'treasureHuntPlays');
      const q = query(playsRef, where('shippingRequested', '==', false));
      const snapshot = await getDocs(q);
      
      // Also get items without shippingRequested field (legacy)
      const allPlaysSnapshot = await getDocs(collection(db, 'users', currentUser.uid, 'treasureHuntPlays'));
      
      const winnings = allPlaysSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(play => 
          (play.status === 'pending_shipment' || play.status === 'won') && 
          !play.shippingRequested
        )
        .sort((a, b) => b.playedAt?.toDate() - a.playedAt?.toDate());
      
      setPendingWinnings(winnings);
    } catch (err) {
      console.error('Error fetching winnings:', err);
      setError('Failed to load your winnings');
    } finally {
      setLoading(false);
    }
  };

  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const selectAll = () => {
    if (selectedItems.length === pendingWinnings.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(pendingWinnings.map(w => w.id));
    }
  };

  const calculateTotal = () => {
    const itemsValue = pendingWinnings
      .filter(w => selectedItems.includes(w.id))
      .reduce((sum, w) => sum + (w.itemValue || 0), 0);
    return {
      itemsValue,
      shippingFee: SHIPPING_FEE,
      total: SHIPPING_FEE
    };
  };

  const handleShippingInfoChange = (e) => {
    const { name, value } = e.target;
    setShippingInfo(prev => ({ ...prev, [name]: value }));
  };

  const validateShippingInfo = () => {
    const required = ['name', 'address', 'city', 'state', 'zipCode'];
    for (const field of required) {
      if (!shippingInfo[field]?.trim()) {
        setError(`Please enter your ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
        return false;
      }
    }
    return true;
  };

  const handleProceedToShipping = () => {
    if (selectedItems.length === 0) {
      setError('Please select at least one item to ship');
      return;
    }
    setError('');
    setStep('shipping');
  };

  const handleProceedToPayment = async () => {
    if (!validateShippingInfo()) return;
    
    setSubmitting(true);
    setError('');

    try {
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe failed to load');
      }
      
      const totals = calculateTotal();
      const selectedWinnings = pendingWinnings.filter(w => selectedItems.includes(w.id));

      console.log('Creating shipping request...');
      
      // Create shipping request in Firestore
      const shippingRequestRef = await addDoc(collection(db, 'clawShippingRequests'), {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        items: selectedWinnings.map(w => ({
          playId: w.id,
          itemName: w.itemName,
          itemValue: w.itemValue,
          itemImage: w.itemImage
        })),
        shippingInfo,
        shippingFee: totals.shippingFee,
        status: 'pending_payment',
        createdAt: serverTimestamp()
      });

      console.log('Shipping request created:', shippingRequestRef.id);
      console.log('Creating Stripe session...');

      // Create Stripe checkout session for shipping fee
      const response = await fetch('https://benevolent-sfogliatella-83141e.netlify.app/.netlify/functions/create-claw-shipping-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerEmail: currentUser.email,
          userId: currentUser.uid,
          shippingRequestId: shippingRequestRef.id,
          shippingFee: totals.shippingFee,
          itemCount: selectedItems.length,
          successUrl: `${window.location.origin}/treasure-hunt?shipping=success&request_id=${shippingRequestRef.id}`,
          cancelUrl: `${window.location.origin}/treasure-hunt?shipping=canceled`
        })
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Netlify function error:', errorText);
        throw new Error(`Server error: ${response.status}`);
      }

      const session = await response.json();
      console.log('Stripe session:', session);

      if (session.error) {
        throw new Error(session.error);
      }

      if (!session.id) {
        throw new Error('No session ID returned from server');
      }

      // Redirect to Stripe Checkout
      console.log('Redirecting to Stripe checkout...');
      const result = await stripe.redirectToCheckout({ sessionId: session.id });

      if (result.error) {
        throw new Error(result.error.message);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(`Payment failed: ${err.message}`);
      setSubmitting(false);
    }
  };

  // Mark items as shipping requested (called after successful payment via URL param)
  const markItemsAsShippingRequested = async (requestId) => {
    try {
      // This would be called from the parent component after successful payment
      // Update each selected play
      for (const itemId of selectedItems) {
        await updateDoc(doc(db, 'users', currentUser.uid, 'treasureHuntPlays', itemId), {
          shippingRequested: true,
          shippingRequestId: requestId,
          shippingRequestedAt: serverTimestamp()
        });
      }
    } catch (err) {
      console.error('Error updating items:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <FaShippingFast /> Request Shipping
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <FaTimes />
          </CloseButton>
        </ModalHeader>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        {loading ? (
          <LoadingState>
            <FaSpinner className="spin" />
            <p>Loading your winnings...</p>
          </LoadingState>
        ) : step === 'select' ? (
          <>
            <StepIndicator>
              <Step $active>1. Select Items</Step>
              <Step>2. Shipping Info</Step>
              <Step>3. Payment</Step>
            </StepIndicator>

            {pendingWinnings.length === 0 ? (
              <EmptyState>
                <FaBox size={50} />
                <p>No pending winnings to ship!</p>
                <p>Play the claw game to win prizes.</p>
              </EmptyState>
            ) : (
              <>
                <SelectAllRow>
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedItems.length === pendingWinnings.length}
                      onChange={selectAll}
                    />
                    Select All ({pendingWinnings.length} items)
                  </label>
                </SelectAllRow>

                <ItemsList>
                  {pendingWinnings.map(item => (
                    <ItemCard 
                      key={item.id} 
                      $selected={selectedItems.includes(item.id)}
                      onClick={() => toggleItemSelection(item.id)}
                    >
                      <ItemCheckbox>
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.id)}
                          onChange={() => toggleItemSelection(item.id)}
                        />
                      </ItemCheckbox>
                      {item.itemImage ? (
                        <ItemImage src={item.itemImage} alt={item.itemName} />
                      ) : (
                        <ItemImagePlaceholder>📦</ItemImagePlaceholder>
                      )}
                      <ItemInfo>
                        <ItemName>{item.itemName}</ItemName>
                        <ItemValue>Value: ${item.itemValue?.toFixed(2) || '0.00'}</ItemValue>
                        <ItemDate>
                          Won: {item.playedAt?.toDate()?.toLocaleDateString() || 'Recently'}
                        </ItemDate>
                      </ItemInfo>
                    </ItemCard>
                  ))}
                </ItemsList>

                <TotalSection>
                  <TotalRow>
                    <span>Selected Items:</span>
                    <span>{selectedItems.length}</span>
                  </TotalRow>
                  <TotalRow>
                    <span>Items Value:</span>
                    <span>${calculateTotal().itemsValue.toFixed(2)}</span>
                  </TotalRow>
                  <TotalRow $highlight>
                    <span>Shipping Fee:</span>
                    <span>${SHIPPING_FEE.toFixed(2)}</span>
                  </TotalRow>
                </TotalSection>

                <ButtonGroup>
                  <CancelButton onClick={onClose}>Cancel</CancelButton>
                  <ProceedButton 
                    onClick={handleProceedToShipping}
                    disabled={selectedItems.length === 0}
                  >
                    Continue to Shipping
                  </ProceedButton>
                </ButtonGroup>
              </>
            )}
          </>
        ) : step === 'shipping' ? (
          <>
            <StepIndicator>
              <Step $completed><FaCheckCircle /></Step>
              <Step $active>2. Shipping Info</Step>
              <Step>3. Payment</Step>
            </StepIndicator>

            <ShippingForm>
              <FormGroup>
                <Label>Full Name *</Label>
                <Input
                  type="text"
                  name="name"
                  value={shippingInfo.name}
                  onChange={handleShippingInfoChange}
                  placeholder="John Doe"
                />
              </FormGroup>

              <FormGroup>
                <Label>Street Address *</Label>
                <Input
                  type="text"
                  name="address"
                  value={shippingInfo.address}
                  onChange={handleShippingInfoChange}
                  placeholder="123 Main St"
                />
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <Label>City *</Label>
                  <Input
                    type="text"
                    name="city"
                    value={shippingInfo.city}
                    onChange={handleShippingInfoChange}
                    placeholder="Denver"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>State *</Label>
                  <Input
                    type="text"
                    name="state"
                    value={shippingInfo.state}
                    onChange={handleShippingInfoChange}
                    placeholder="CO"
                    maxLength={2}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>ZIP Code *</Label>
                  <Input
                    type="text"
                    name="zipCode"
                    value={shippingInfo.zipCode}
                    onChange={handleShippingInfoChange}
                    placeholder="80202"
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label>Phone (optional)</Label>
                <Input
                  type="tel"
                  name="phone"
                  value={shippingInfo.phone}
                  onChange={handleShippingInfoChange}
                  placeholder="(555) 123-4567"
                />
              </FormGroup>
            </ShippingForm>

            <TotalSection>
              <TotalRow>
                <span>Items to Ship:</span>
                <span>{selectedItems.length}</span>
              </TotalRow>
              <TotalRow $highlight>
                <span>Shipping Fee:</span>
                <span>${SHIPPING_FEE.toFixed(2)}</span>
              </TotalRow>
            </TotalSection>

            <ButtonGroup>
              <BackButton onClick={() => setStep('select')}>Back</BackButton>
              <ProceedButton 
                onClick={handleProceedToPayment}
                disabled={submitting}
              >
                {submitting ? <><FaSpinner className="spin" /> Processing...</> : `Pay $${SHIPPING_FEE.toFixed(2)} Shipping`}
              </ProceedButton>
            </ButtonGroup>
          </>
        ) : null}
      </ModalContent>
    </ModalOverlay>
  );
};

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: #1a1a2e;
  border-radius: 20px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  border: 2px solid #D4AF37;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid rgba(212, 175, 55, 0.3);
`;

const ModalTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #D4AF37;
  margin: 0;
  font-size: 1.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #aaa;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  
  &:hover {
    color: #D4AF37;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: rgba(0, 0, 0, 0.2);
`;

const Step = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.$active ? '#D4AF37' : props.$completed ? '#4CAF50' : '#666'};
  font-weight: ${props => props.$active ? 'bold' : 'normal'};
  font-size: 0.9rem;
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #D4AF37;
  
  .spin {
    animation: spin 1s linear infinite;
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #aaa;
  text-align: center;
  
  svg {
    margin-bottom: 1rem;
    color: #666;
  }
`;

const SelectAllRow = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  label {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: #ddd;
    cursor: pointer;
    
    input {
      width: 18px;
      height: 18px;
      cursor: pointer;
    }
  }
`;

const ItemsList = styled.div`
  padding: 1rem 1.5rem;
  max-height: 300px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ItemCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: ${props => props.$selected ? 'rgba(212, 175, 55, 0.15)' : 'rgba(255, 255, 255, 0.05)'};
  border: 2px solid ${props => props.$selected ? '#D4AF37' : 'transparent'};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(212, 175, 55, 0.1);
  }
`;

const ItemCheckbox = styled.div`
  input {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
`;

const ItemImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: contain;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
`;

const ItemImagePlaceholder = styled.div`
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
`;

const ItemInfo = styled.div`
  flex: 1;
`;

const ItemName = styled.div`
  color: white;
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const ItemValue = styled.div`
  color: #4CAF50;
  font-size: 0.9rem;
`;

const ItemDate = styled.div`
  color: #888;
  font-size: 0.8rem;
`;

const TotalSection = styled.div`
  padding: 1rem 1.5rem;
  background: rgba(0, 0, 0, 0.2);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  color: ${props => props.$highlight ? '#D4AF37' : '#ddd'};
  font-weight: ${props => props.$highlight ? 'bold' : 'normal'};
  font-size: ${props => props.$highlight ? '1.1rem' : '1rem'};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 1rem;
  background: transparent;
  color: #aaa;
  border: 1px solid #666;
  border-radius: 10px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: white;
  }
`;

const BackButton = styled(CancelButton)``;

const ProceedButton = styled.button`
  flex: 2;
  padding: 1rem;
  background: linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%);
  color: #1a1a2e;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover:not(:disabled) {
    transform: scale(1.02);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .spin {
    animation: spin 1s linear infinite;
  }
`;

const ShippingForm = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  color: #aaa;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #D4AF37;
  }
  
  &::placeholder {
    color: #666;
  }
`;

const ErrorMessage = styled.div`
  margin: 1rem 1.5rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: 8px;
  color: #ff6b6b;
`;

export default ClawWinningsShipping;
